import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

import type {
	MaterialMovementReportParams,
	MovementAction,
} from '../../../../redux/api/material-movements';
import {
	ACTION_ORDER,
	SHOP_TIMEZONE,
} from '../../../../components/MaterialMovements/movementMeta';

dayjs.extend(utc);
dayjs.extend(timezone);

export const PRESETS = [
	'today',
	'yesterday',
	'this_week',
	'last_week',
	'this_month',
	'custom',
] as const;
export type Preset = (typeof PRESETS)[number];

export const PRESET_LABELS: Record<Preset, string> = {
	today: 'Today',
	yesterday: 'Yesterday',
	this_week: 'This Week',
	last_week: 'Last Week',
	this_month: 'This Month',
	custom: 'Custom',
};

export interface MovementFilters {
	preset: Preset;
	dateFrom: string;
	dateTo: string;
	performedBy: number[];
	actions: MovementAction[];
	bar: string;
	buildings: string[];
}

const DAY = 'YYYY-MM-DD';

export const shopToday = (now: Dayjs = dayjs()) =>
	now.tz(SHOP_TIMEZONE).startOf('day');

/** Weeks run Sunday through Saturday, regardless of the browser's locale. */
const sundayOf = (day: Dayjs) => day.subtract(day.day(), 'day');

export const presetRange = (
	preset: Exclude<Preset, 'custom'>,
	now: Dayjs = dayjs()
): { dateFrom: string; dateTo: string } => {
	const today = shopToday(now);
	const range = (from: Dayjs, to: Dayjs) => ({
		dateFrom: from.format(DAY),
		dateTo: to.format(DAY),
	});
	switch (preset) {
		case 'yesterday':
			return range(today.subtract(1, 'day'), today.subtract(1, 'day'));
		case 'this_week':
			return range(sundayOf(today), today);
		case 'last_week': {
			const lastSunday = sundayOf(today).subtract(7, 'day');
			return range(lastSunday, lastSunday.add(6, 'day'));
		}
		case 'this_month':
			return range(today.startOf('month'), today);
		case 'today':
		default:
			return range(today, today);
	}
};

export const defaultFilters = (now: Dayjs = dayjs()): MovementFilters => ({
	preset: 'today',
	...presetRange('today', now),
	performedBy: [],
	actions: [],
	bar: '',
	buildings: [],
});

const isDay = (value: string | null): value is string =>
	Boolean(value && /^\d{4}-\d{2}-\d{2}$/.test(value) && dayjs(value).isValid());

const list = (value: string | null) =>
	value ? value.split(',').map((v) => v.trim()).filter(Boolean) : [];

/**
 * A named preset is re-evaluated on load, so a bookmarked "Today" means today
 * rather than the day it was bookmarked. Only Custom pins its dates.
 */
export const filtersFromSearchParams = (
	params: URLSearchParams,
	now: Dayjs = dayjs()
): MovementFilters => {
	const base = defaultFilters(now);
	const rawPreset = params.get('preset') as Preset | null;
	const preset = rawPreset && PRESETS.includes(rawPreset) ? rawPreset : null;
	let dates = { dateFrom: base.dateFrom, dateTo: base.dateTo };
	let resolved: Preset = 'today';
	const from = params.get('from');
	const to = params.get('to');
	if (preset && preset !== 'custom') {
		dates = presetRange(preset, now);
		resolved = preset;
	} else if (isDay(from) && isDay(to)) {
		dates = from <= to ? { dateFrom: from, dateTo: to } : { dateFrom: to, dateTo: from };
		resolved = 'custom';
	}
	return {
		preset: resolved,
		...dates,
		performedBy: list(params.get('operators'))
			.map(Number)
			.filter((n) => Number.isFinite(n)),
		actions: list(params.get('actions')).filter((a): a is MovementAction =>
			ACTION_ORDER.includes(a as MovementAction)
		),
		bar: params.get('bar') ?? '',
		buildings: list(params.get('buildings')),
	};
};

export const filtersToSearchParams = (filters: MovementFilters): URLSearchParams => {
	const params = new URLSearchParams();
	params.set('preset', filters.preset);
	if (filters.preset === 'custom') {
		params.set('from', filters.dateFrom);
		params.set('to', filters.dateTo);
	}
	if (filters.performedBy.length) params.set('operators', filters.performedBy.join(','));
	if (filters.actions.length) params.set('actions', filters.actions.join(','));
	if (filters.bar.trim()) params.set('bar', filters.bar.trim());
	if (filters.buildings.length) params.set('buildings', filters.buildings.join(','));
	return params;
};

export const toReportParams = (
	filters: MovementFilters
): MaterialMovementReportParams => ({
	date_from: filters.dateFrom,
	date_to: filters.dateTo,
	performed_by: filters.performedBy,
	actions: filters.actions,
	bar: filters.bar,
	building_ids: filters.buildings,
});

export const formatRange = (dateFrom: string, dateTo: string) => {
	const from = dayjs(dateFrom);
	const to = dayjs(dateTo);
	if (dateFrom === dateTo) return from.format('ddd, MMM D, YYYY');
	const sameYear = from.year() === to.year();
	return `${from.format(sameYear ? 'MMM D' : 'MMM D, YYYY')} – ${to.format('MMM D, YYYY')}`;
};
